import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Stack,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  Radio,
  FormControlLabel,
  FormLabel,
  Alert,
  LinearProgress,
  Card,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { Close, Delete, Add, Save, Lock, AccessTime } from "@mui/icons-material";
import { UserProfile, PreferredAge } from "../types/profile";
import { updateFreeFields, submitReviewFields } from "../api/profileApi";
import { apiClient, API_ENDPOINTS } from "../../../config/api";
import { uploadMultipleImages, UploadProgress } from "../../../utils/imageUpload";
import type {
  Interest as MetaInterest,
  Region as MetaRegion,
  RegionsResponse,
  InterestsResponse,
  ProfileImageDto,
} from "../../../types";
import type { ProfilePhoto } from "../types/profile";

// -- Form state types --

interface ResidenceRegionForm {
  level1Id: number;
  level2Id: number;
  level1Name: string;
  level2Name: string;
}

interface PreferredRegionForm {
  level1Id: number;
  level2Id: number;
  level1Name: string;
  level2Name: string;
}

interface EditFormData {
  jobTitle: string;
  oneLiner: string;
  selfIntro: string;
  preferredAge: PreferredAge;
  interestIds: number[];
  residenceRegion: ResidenceRegionForm;
  preferredRegions: PreferredRegionForm[];
}

// -- Pending review field type label --
const FIELD_TYPE_LABELS: Record<string, string> = {
  PHOTOS: "사진",
  // NICKNAME: "닉네임", // Phase 2: 서버에서 현재 미반환
  JOB: "직업",
  ONE_LINER: "한줄소개",
  SELF_INTRO: "자기소개",
};

// -- Field type badge helpers --

const ReviewFieldBadge = () => (
  <Chip
    label="심사 후 반영"
    size="small"
    sx={{ ml: 1, bgcolor: "#FFF3E0", color: "#BF360C", fontSize: "0.68rem", height: 20, fontWeight: 600 }}
  />
);

const FreeFieldBadge = () => (
  <Chip
    label="즉시 반영"
    size="small"
    sx={{ ml: 1, bgcolor: "#E8F5E9", color: "#1B5E20", fontSize: "0.68rem", height: 20, fontWeight: 600 }}
  />
);

// -- Component --

type Props = {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  profile: UserProfile;
  submitLabel?: string;
  submittingLabel?: string;
};

const preferredAgeLabels: Record<string, string> = {
  [PreferredAge.YOUNGER]: "연하",
  [PreferredAge.OLDER]: "연상",
  [PreferredAge.ANY]: "상관없음",
};

export default function EditProfileModal({
  open,
  onClose,
  onSave,
  profile,
  submitLabel = "저장",
  submittingLabel = "저장 중...",
}: Props) {
  // ---- Derived state ----
  const hasPendingReview = Boolean(profile.pendingReview?.hasPending);
  const residenceChangesLeft = Math.max(0, 2 - (profile.residenceChangeCountThisYear || 0));

  // ---- Form state ----
  const [formData, setFormData] = React.useState<EditFormData>({
    jobTitle: "",
    oneLiner: "",
    selfIntro: "",
    preferredAge: PreferredAge.ANY,
    interestIds: [],
    residenceRegion: { level1Id: 0, level2Id: 0, level1Name: "", level2Name: "" },
    preferredRegions: [],
  });

  // ---- Photo state ----
  const [existingPhotos, setExistingPhotos] = React.useState<ProfilePhoto[]>([]);
  const [newPhotoFiles, setNewPhotoFiles] = React.useState<File[]>([]);
  const [deletedStorageKeys, setDeletedStorageKeys] = React.useState<string[]>([]);

  // ---- Meta data ----
  const [interests, setInterests] = React.useState<MetaInterest[]>([]);
  const [level1Regions, setLevel1Regions] = React.useState<MetaRegion[]>([]);
  const [level2Regions, setLevel2Regions] = React.useState<MetaRegion[]>([]);
  const [metaLoading, setMetaLoading] = React.useState(false);

  // ---- Saving / upload ----
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [successMessage, setSuccessMessage] = React.useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = React.useState<UploadProgress[]>([]);

  // ---- Preferred region dialog ----
  const [showAddRegionDialog, setShowAddRegionDialog] = React.useState(false);
  const [regionDialogLevel1Id, setRegionDialogLevel1Id] = React.useState(0);
  const [regionDialogLevel2Id, setRegionDialogLevel2Id] = React.useState(0);
  const [regionDialogLevel2Regions, setRegionDialogLevel2Regions] = React.useState<MetaRegion[]>([]);

  const totalPhotos = existingPhotos.length + newPhotoFiles.length;

  // =========================================================
  // useEffect: Initialize form when profile changes or modal opens
  // =========================================================
  React.useEffect(() => {
    if (!open || !profile) return;

    setFormData({
      jobTitle: profile.jobTitle || "",
      oneLiner: profile.oneLiner || "",
      selfIntro: profile.selfIntro || "",
      preferredAge: profile.preferredAge,
      interestIds: profile.interests.map((i) => i.id),
      residenceRegion: {
        level1Id: profile.residenceRegion?.level1Id || 0,
        level2Id: profile.residenceRegion?.level2Id || 0,
        level1Name: profile.residenceRegion?.level1Name || "",
        level2Name: profile.residenceRegion?.level2Name || "",
      },
      preferredRegions: profile.preferredRegions.map((r) => ({
        level1Id: r.level1Id,
        level2Id: r.level2Id,
        level1Name: r.level1Name,
        level2Name: r.level2Name,
      })),
    });
    setExistingPhotos([...profile.profilePhotos]);
    setNewPhotoFiles([]);
    setDeletedStorageKeys([]);
    setError(null);
    setSuccessMessage(null);
    setUploadProgress([]);
  }, [open, profile]);

  // =========================================================
  // useEffect: Load meta data (interests + level1 regions)
  // =========================================================
  React.useEffect(() => {
    if (!open) return;

    const loadMetaData = async () => {
      try {
        setMetaLoading(true);
        const [interestsRes, regionsRes] = await Promise.all([
          apiClient.get<InterestsResponse>(API_ENDPOINTS.META.INTERESTS),
          apiClient.get<RegionsResponse>(`${API_ENDPOINTS.META.REGIONS}?level=1`),
        ]);

        const activeInterests = interestsRes.result.interests
          .filter((interest) => interest.isActive)
          .sort((a, b) => a.sortOrder - b.sortOrder);

        const activeLevel1Regions = regionsRes.result.regions
          .filter((region) => region.isActive)
          .sort((a, b) => a.sortOrder - b.sortOrder);

        setInterests(activeInterests);
        setLevel1Regions(activeLevel1Regions);
      } catch (err) {
        console.error("메타 데이터 로딩 실패:", err);
      } finally {
        setMetaLoading(false);
      }
    };

    loadMetaData();
  }, [open]);

  // =========================================================
  // useEffect: Load level2 regions when level1 changes
  // =========================================================
  React.useEffect(() => {
    const loadLevel2Regions = async () => {
      if (formData.residenceRegion.level1Id > 0) {
        try {
          const response = await apiClient.get<RegionsResponse>(
            `${API_ENDPOINTS.META.REGIONS}?level=2&parentId=${formData.residenceRegion.level1Id}`
          );

          const activeLevel2Regions = response.result.regions
            .filter((region) => region.isActive)
            .sort((a, b) => a.sortOrder - b.sortOrder);

          setLevel2Regions(activeLevel2Regions);
        } catch (err) {
          console.error("Level 2 지역 로딩 실패:", err);
        }
      } else {
        setLevel2Regions([]);
      }
    };

    loadLevel2Regions();
  }, [formData.residenceRegion.level1Id]);

  // =========================================================
  // Photo management
  // =========================================================
  const handleRemoveExistingPhoto = (photoId: number) => {
    const photoToRemove = existingPhotos.find((p) => p.id === photoId);
    if (photoToRemove) {
      try {
        const url = new URL(photoToRemove.endpointUrl);
        const storageKey = url.pathname.startsWith("/")
          ? url.pathname.substring(1)
          : url.pathname;
        setDeletedStorageKeys((prev) => [...prev, storageKey]);
      } catch {
        const parts = photoToRemove.endpointUrl.split("/");
        setDeletedStorageKeys((prev) => [...prev, parts.slice(3).join("/")]);
      }
    }
    setExistingPhotos((prev) => prev.filter((p) => p.id !== photoId));
  };

  const handleAddNewPhotos = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    const currentTotal = existingPhotos.length + newPhotoFiles.length;
    const totalAfterAdd = currentTotal + files.length;

    if (totalAfterAdd > 10) {
      const maxCanAdd = 10 - currentTotal;
      setError(
        `최대 10장까지 가능해요. 지금 ${currentTotal}장이 있어서 ${maxCanAdd}장만 더 추가할 수 있어요.`
      );
      event.target.value = "";
      return;
    }

    const allowedTypes = [
      "image/jpeg", "image/jpg", "image/png", "image/webp",
      "image/heic", "image/heif", "image/avif", "image/bmp",
      "image/gif", "image/tiff", "image/svg+xml",
    ];
    const MAX_SIZE = 10 * 1024 * 1024;
    const invalidFiles: string[] = [];

    files.forEach((file, index) => {
      if (file.size > MAX_SIZE) {
        invalidFiles.push(`사진 ${index + 1}: 파일 크기가 10MB를 초과해요. (${(file.size / 1024 / 1024).toFixed(1)}MB)`);
      } else if (!allowedTypes.includes(file.type)) {
        invalidFiles.push(`사진 ${index + 1}: 지원하지 않는 파일 형식이에요. (${file.type})`);
      }
    });

    if (invalidFiles.length > 0) {
      setError(invalidFiles.join("\n"));
      event.target.value = "";
      return;
    }

    setNewPhotoFiles((prev) => [...prev, ...files]);
    setError(null);
    event.target.value = "";
  };

  const handleRemoveNewPhoto = (index: number) => {
    setNewPhotoFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // =========================================================
  // Interest toggle
  // =========================================================
  const handleInterestToggle = (interestId: number) => {
    setFormData((prev) => ({
      ...prev,
      interestIds: prev.interestIds.includes(interestId)
        ? prev.interestIds.filter((id) => id !== interestId)
        : [...prev.interestIds, interestId],
    }));
  };

  // =========================================================
  // Preferred region dialog
  // =========================================================
  const handleRegionDialogLevel1Change = async (level1Id: number) => {
    setRegionDialogLevel1Id(level1Id);
    setRegionDialogLevel2Id(0);

    if (!level1Id) {
      setRegionDialogLevel2Regions([]);
      return;
    }

    try {
      const response = await apiClient.get<RegionsResponse>(
        `${API_ENDPOINTS.META.REGIONS}?level=2&parentId=${level1Id}`
      );

      const activeLevel2Regions = response.result.regions
        .filter((region) => region.isActive)
        .sort((a, b) => a.sortOrder - b.sortOrder);
      setRegionDialogLevel2Regions(activeLevel2Regions);
    } catch (err) {
      console.error("선호 지역 Level2 로드 실패:", err);
    }
  };

  const handleAddPreferredRegion = () => {
    if (!regionDialogLevel1Id || !regionDialogLevel2Id) return;

    const level1Region = level1Regions.find((r) => r.id === regionDialogLevel1Id);
    const level2Region = regionDialogLevel2Regions.find((r) => r.id === regionDialogLevel2Id);

    if (!level1Region || !level2Region) return;

    const newRegion: PreferredRegionForm = {
      level1Id: regionDialogLevel1Id,
      level2Id: regionDialogLevel2Id,
      level1Name: level1Region.nameKo,
      level2Name: level2Region.nameKo,
    };

    const isDuplicate = formData.preferredRegions.some(
      (region) => region.level1Id === newRegion.level1Id && region.level2Id === newRegion.level2Id
    );

    if (isDuplicate) return;

    setFormData((prev) => ({
      ...prev,
      preferredRegions: [...prev.preferredRegions, newRegion],
    }));

    setShowAddRegionDialog(false);
    setRegionDialogLevel1Id(0);
    setRegionDialogLevel2Id(0);
    setRegionDialogLevel2Regions([]);
  };

  const handleRemovePreferredRegion = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      preferredRegions: prev.preferredRegions.filter((_, i) => i !== index),
    }));
  };

  const handleCloseRegionDialog = () => {
    setShowAddRegionDialog(false);
    setRegionDialogLevel1Id(0);
    setRegionDialogLevel2Id(0);
    setRegionDialogLevel2Regions([]);
  };

  // =========================================================
  // Save handler — split: 자유 필드 vs 심사 필드
  // =========================================================
  const handleSave = async () => {
    // Detect review field changes
    const hasReviewPhotoChanges = newPhotoFiles.length > 0 || deletedStorageKeys.length > 0;
    const hasReviewTextChanges =
      formData.jobTitle !== (profile.jobTitle || "") ||
      formData.oneLiner !== (profile.oneLiner || "") ||
      formData.selfIntro !== (profile.selfIntro || "");
    const hasReviewChanges = hasReviewPhotoChanges || hasReviewTextChanges;

    // Detect free field changes
    const hasFreeChanges =
      formData.preferredAge !== profile.preferredAge ||
      formData.residenceRegion.level1Id !== (profile.residenceRegion?.level1Id || 0) ||
      formData.residenceRegion.level2Id !== (profile.residenceRegion?.level2Id || 0) ||
      JSON.stringify([...formData.interestIds].sort()) !==
        JSON.stringify(profile.interests.map((i) => i.id).sort()) ||
      JSON.stringify(
        formData.preferredRegions
          .map((r) => ({ l1: r.level1Id, l2: r.level2Id }))
          .sort((a, b) => a.l1 - b.l1)
      ) !==
        JSON.stringify(
          profile.preferredRegions
            .map((r) => ({ l1: r.level1Id, l2: r.level2Id }))
            .sort((a, b) => a.l1 - b.l1)
        );

    if (!hasReviewChanges && !hasFreeChanges) {
      setError("변경된 내용이 없어요.");
      return;
    }

    // 심사 중 review 필드 재수정 차단
    if (hasReviewChanges && hasPendingReview) {
      setError("현재 담당자가 검토 중이에요. 심사가 완료된 후 수정할 수 있어요.");
      return;
    }

    // Validation
    if (existingPhotos.length + newPhotoFiles.length < 3) {
      setError("사진을 3장 이상 등록해주세요.");
      return;
    }

    if (formData.residenceRegion.level1Id === 0 || formData.residenceRegion.level2Id === 0) {
      setError("거주 지역을 선택해주세요.");
      return;
    }

    if (formData.oneLiner && formData.oneLiner.length > 64) {
      setError("한줄소개는 64자 이하여야 해요.");
      return;
    }

    if (formData.selfIntro && formData.selfIntro.length > 0 && formData.selfIntro.length < 10) {
      setError("자기소개는 10자 이상이어야 해요.");
      return;
    }

    if (formData.selfIntro && formData.selfIntro.length > 512) {
      setError("자기소개는 512자 이하여야 해요.");
      return;
    }

    setSaving(true);
    setError(null);
    setSuccessMessage(null);

    try {
      // 1. 자유 필드 저장 (즉시 반영)
      const freeFieldsPromise = hasFreeChanges
        ? updateFreeFields({
            interestIds: formData.interestIds,
            preferredAge: formData.preferredAge,
            residenceRegion: {
              level1Id: formData.residenceRegion.level1Id,
              level2Id: formData.residenceRegion.level2Id,
            },
            preferredRegions: formData.preferredRegions.map((r) => ({
              level1Id: r.level1Id,
              level2Id: r.level2Id,
            })),
          })
        : Promise.resolve();

      // 2. 심사 필드 제출 (사진 업로드 후 심사 요청)
      const reviewFieldsPromise = hasReviewChanges
        ? (async () => {
            let newUploadedImages: ProfileImageDto[] = [];
            if (newPhotoFiles.length > 0) {
              newUploadedImages = await uploadMultipleImages(newPhotoFiles, (progresses) =>
                setUploadProgress(progresses)
              );
            }
            await submitReviewFields({
              jobTitle: formData.jobTitle !== (profile.jobTitle || "") ? formData.jobTitle : undefined,
              oneLiner: formData.oneLiner !== (profile.oneLiner || "") ? formData.oneLiner : undefined,
              selfIntro: formData.selfIntro !== (profile.selfIntro || "") ? formData.selfIntro : undefined,
              profileImages: newUploadedImages.length > 0 ? newUploadedImages : undefined,
              deletedStorageKeys: deletedStorageKeys.length > 0 ? deletedStorageKeys : undefined,
            });
          })()
        : Promise.resolve();

      await Promise.all([freeFieldsPromise, reviewFieldsPromise]);

      // Success message
      if (hasReviewChanges && hasFreeChanges) {
        setSuccessMessage(
          "관심사·선호 조건은 바로 반영됐어요. 사진·직업·소개글은 담당자 확인 후 반영됩니다."
        );
      } else if (hasReviewChanges) {
        setSuccessMessage("심사 요청이 완료됐어요. 보통 24시간 이내에 검토됩니다.");
      } else {
        setSuccessMessage("저장됐어요.");
      }

      onSave();
      if (!hasReviewChanges) {
        onClose();
      }
    } catch (err: any) {
      setError(err.message || "저장에 실패했습니다.");
    } finally {
      setSaving(false);
    }
  };

  // =========================================================
  // Helpers
  // =========================================================
  const calculateAge = (birthDate?: string) => {
    if (!birthDate) return null;
    const birth = new Date(birthDate);
    const today = new Date();
    const age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) {
      return age - 1;
    }
    return age;
  };

  const genderLabel = (gender?: string) => {
    if (gender === "MALE") return "남성";
    if (gender === "FEMALE") return "여성";
    return "미설정";
  };

  const age = calculateAge(profile.birthDate);

  // =========================================================
  // Render
  // =========================================================
  return (
    <>
      <Dialog
        open={open}
        onClose={saving ? undefined : onClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "20px",
            maxHeight: "90vh",
            m: { xs: 1, sm: 2 },
            overflow: "hidden",
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          {/* Header */}
          <Box
            sx={{
              background: "linear-gradient(135deg, #0D1F3C, #1A3360)",
              p: 3,
              pb: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography
              variant="h5"
              sx={{ fontWeight: 700, fontFamily: "'Playfair Display', serif", color: "#FFFFFF" }}
            >
              프로필 수정
            </Typography>
            <IconButton
              onClick={onClose}
              size="small"
              disabled={saving}
              sx={{ color: "rgba(255,255,255,0.8)" }}
            >
              <Close />
            </IconButton>
          </Box>

          {/* Content */}
          <Box sx={{ px: 3, pb: 3 }}>
            {/* 심사 대기 중 배너 */}
            {hasPendingReview && (
              <Alert
                severity="info"
                icon={<AccessTime />}
                sx={{ mt: 2, mb: 0, borderRadius: 2 }}
              >
                <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                  담당자가 확인하고 있어요
                </Typography>
                <Typography variant="body2">
                  {profile.pendingReview?.fieldTypes?.length
                    ? `${profile.pendingReview.fieldTypes.map((t) => FIELD_TYPE_LABELS[t] ?? t).join(", ")} 수정 건을 심사 중입니다.`
                    : "프로필 수정 건을 심사 중입니다."}{" "}
                  보통 24시간 이내에 완료돼요.
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                  심사 완료 전까지 해당 항목은 수정할 수 없어요.
                </Typography>
              </Alert>
            )}

            {/* 성공 메시지 */}
            {successMessage && (
              <Alert severity="success" sx={{ mt: 2, mb: 0, borderRadius: 2 }}>
                {successMessage}
              </Alert>
            )}

            {/* 에러 메시지 */}
            {error && (
              <Alert severity="error" sx={{ mt: 2, mb: 0, whiteSpace: "pre-line", borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            {/* 메타 데이터 로딩 */}
            {metaLoading && <LinearProgress sx={{ mt: 2 }} />}

            <Stack spacing={4} sx={{ mt: 3 }}>
              {/* ---- Section: 프로필 사진 (심사 필드) ---- */}
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    프로필 사진
                  </Typography>
                  <ReviewFieldBadge />
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  최소 3장, 최대 10장 (현재 {totalPhotos}/10)
                </Typography>

                {hasPendingReview ? (
                  <Box>
                    {/* 현재 상대방에게 보이는 승인된 사진 */}
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      현재 상대방에게 보이는 사진
                    </Typography>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)" },
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      {existingPhotos
                        .sort((a, b) => a.sortOrder - b.sortOrder)
                        .map((photo, index) => (
                          <Card
                            key={`approved-${photo.id}`}
                            sx={{ position: "relative", aspectRatio: "1", overflow: "hidden", opacity: 0.85 }}
                          >
                            <Box
                              component="img"
                              src={photo.endpointUrl}
                              alt={`현재 사진 ${index + 1}`}
                              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                            />
                            {index === 0 && (
                              <Chip
                                label="대표"
                                size="small"
                                color="primary"
                                sx={{ position: "absolute", top: 8, left: 8, fontWeight: 600, fontSize: "0.7rem" }}
                              />
                            )}
                          </Card>
                        ))}
                    </Box>

                    {/* 심사 중인 새 사진 미리보기 */}
                    {profile.pendingReview?.pendingPhotos && profile.pendingReview.pendingPhotos.length > 0 && (
                      <Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                          <AccessTime sx={{ fontSize: 18, color: "warning.main" }} />
                          <Typography variant="body2" color="warning.dark" fontWeight={600}>
                            새 사진 심사 중 · 보통 24시간 이내 완료
                          </Typography>
                        </Box>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)" },
                            gap: 2,
                            mb: 2,
                          }}
                        >
                          {profile.pendingReview.pendingPhotos.map((url, index) => (
                            <Card
                              key={`pending-${index}`}
                              sx={{ position: "relative", aspectRatio: "1", overflow: "hidden", border: "2px solid", borderColor: "warning.light" }}
                            >
                              <Box
                                component="img"
                                src={url}
                                alt={`심사 중 사진 ${index + 1}`}
                                sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                              />
                              <Box
                                sx={{
                                  position: "absolute",
                                  bottom: 0,
                                  left: 0,
                                  right: 0,
                                  bgcolor: "rgba(237,108,2,0.75)",
                                  color: "white",
                                  p: 0.5,
                                  textAlign: "center",
                                }}
                              >
                                <Typography variant="caption" fontWeight={600}>심사 중</Typography>
                              </Box>
                            </Card>
                          ))}
                        </Box>
                      </Box>
                    )}

                    <Alert severity="warning" sx={{ borderRadius: 2 }}>
                      심사가 완료된 후 사진을 다시 수정할 수 있어요.
                    </Alert>
                  </Box>
                ) : (
                  <>
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)" },
                        gap: 2,
                        mb: 2,
                      }}
                    >
                      {/* 기존 사진 */}
                      {existingPhotos
                        .sort((a, b) => a.sortOrder - b.sortOrder)
                        .map((photo, index) => (
                          <Card
                            key={`existing-${photo.id}`}
                            sx={{ position: "relative", aspectRatio: "1", overflow: "hidden" }}
                          >
                            <Box
                              component="img"
                              src={photo.endpointUrl}
                              alt={`사진 ${index + 1}`}
                              sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                            />
                            <Box
                              sx={{
                                position: "absolute",
                                top: 8,
                                right: 8,
                                backgroundColor: "rgba(0,0,0,0.6)",
                                borderRadius: "50%",
                                p: 0.5,
                              }}
                            >
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveExistingPhoto(photo.id)}
                                disabled={totalPhotos <= 3}
                                sx={{ color: "white", p: 0 }}
                                aria-label="사진 삭제"
                              >
                                <Delete sx={{ fontSize: 20 }} />
                              </IconButton>
                            </Box>
                            {index === 0 && (
                              <Chip
                                label="대표"
                                size="small"
                                color="primary"
                                sx={{
                                  position: "absolute",
                                  top: 8,
                                  left: 8,
                                  fontWeight: 600,
                                  fontSize: "0.7rem",
                                }}
                              />
                            )}
                          </Card>
                        ))}

                      {/* 새 사진 미리보기 */}
                      {newPhotoFiles.map((file, index) => (
                        <Card
                          key={`new-${index}`}
                          sx={{ position: "relative", aspectRatio: "1", overflow: "hidden" }}
                        >
                          <Box
                            component="img"
                            src={URL.createObjectURL(file)}
                            alt={`새 사진 ${index + 1}`}
                            sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                          />
                          <Box
                            sx={{
                              position: "absolute",
                              top: 8,
                              right: 8,
                              backgroundColor: "rgba(0,0,0,0.6)",
                              borderRadius: "50%",
                              p: 0.5,
                            }}
                          >
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveNewPhoto(index)}
                              sx={{ color: "white", p: 0 }}
                              aria-label="새 사진 삭제"
                            >
                              <Delete sx={{ fontSize: 20 }} />
                            </IconButton>
                          </Box>
                          <Box
                            sx={{
                              position: "absolute",
                              bottom: 0,
                              left: 0,
                              right: 0,
                              backgroundColor: "rgba(13,31,60,0.7)",
                              color: "white",
                              p: 0.5,
                              textAlign: "center",
                            }}
                          >
                            <Typography variant="caption">새로 추가</Typography>
                          </Box>
                        </Card>
                      ))}

                      {/* 사진 추가 버튼 */}
                      {totalPhotos < 10 && (
                        <Card
                          sx={{
                            aspectRatio: "1",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            border: "2px dashed #ccc",
                            backgroundColor: "#fafafa",
                            cursor: "pointer",
                            "&:hover": { backgroundColor: "#f0f0f0", borderColor: "#999" },
                          }}
                          component="label"
                        >
                          <input
                            accept="image/jpeg,image/jpg,image/png,image/webp,image/heic,image/heif,image/avif,image/bmp,image/gif,image/tiff,image/svg+xml"
                            style={{ display: "none" }}
                            type="file"
                            multiple
                            onChange={handleAddNewPhotos}
                          />
                          <Box sx={{ textAlign: "center" }}>
                            <Add sx={{ fontSize: 40, color: "#999", mb: 1 }} />
                            <Typography variant="body2" color="text.secondary">
                              사진 추가
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ({totalPhotos}/10)
                            </Typography>
                          </Box>
                        </Card>
                      )}
                    </Box>

                    {totalPhotos < 3 && (
                      <Alert severity="warning" sx={{ mb: 1, borderRadius: 2 }}>
                        사진을 3장 이상 등록해주세요. (현재 {totalPhotos}장)
                      </Alert>
                    )}

                    {/* 업로드 진행률 */}
                    {uploadProgress.length > 0 && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" gutterBottom>
                          업로드 중...
                        </Typography>
                        {uploadProgress.map((progress, index) => (
                          <Box key={index} sx={{ mb: 1 }}>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                              <Typography variant="caption">{progress.fileName}</Typography>
                              <Typography variant="caption">
                                {progress.status === "completed" ? "완료" : `${progress.progress}%`}
                              </Typography>
                            </Box>
                            <LinearProgress
                              variant="determinate"
                              value={progress.progress}
                              color={progress.status === "error" ? "error" : "primary"}
                            />
                          </Box>
                        ))}
                      </Box>
                    )}
                  </>
                )}
              </Box>

              {/* ---- Section: 기본 정보 ---- */}
              <Box>
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                  기본 정보
                </Typography>

                <Stack spacing={2}>
                  {/* 잠금 필드: 나이, 성별 */}
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)" },
                      gap: 2,
                    }}
                  >
                    {[
                      {
                        label: "나이",
                        value: age ? `${age}세 (${profile.birthDate})` : "미설정",
                      },
                      { label: "성별", value: genderLabel(profile.gender) },
                    ].map(({ label, value }) => (
                      <Box
                        key={label}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          p: 2,
                          bgcolor: "grey.100",
                          borderRadius: 2,
                          minHeight: 56,
                        }}
                      >
                        <Lock sx={{ fontSize: 16, color: "text.disabled", flexShrink: 0 }} />
                        <Box>
                          <Typography variant="caption" color="text.secondary">
                            {label} (수정 불가 · 고객센터 문의)
                          </Typography>
                          <Typography variant="body1" fontWeight={600}>
                            {value}
                          </Typography>
                        </Box>
                      </Box>
                    ))}
                  </Box>

                  {/* 심사 필드: 직업 */}
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Typography variant="body2" fontWeight={600} color="text.secondary">
                        직업
                      </Typography>
                      <ReviewFieldBadge />
                    </Box>
                    <TextField
                      fullWidth
                      value={formData.jobTitle}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, jobTitle: e.target.value }))
                      }
                      placeholder="예: 교사, 개발자, 프리랜서"
                      disabled={hasPendingReview}
                      inputProps={{ style: { fontSize: "1rem", minHeight: 24 } }}
                      sx={{ "& .MuiInputBase-root": { minHeight: 56 } }}
                    />
                  </Box>

                  {/* 심사 필드: 한줄소개 */}
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Typography variant="body2" fontWeight={600} color="text.secondary">
                        한줄소개
                      </Typography>
                      <ReviewFieldBadge />
                    </Box>
                    <TextField
                      fullWidth
                      value={formData.oneLiner}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, oneLiner: e.target.value }))
                      }
                      placeholder="나를 한줄로 표현해보세요"
                      helperText={`${formData.oneLiner.length}/64자`}
                      inputProps={{ maxLength: 64, style: { fontSize: "1rem" } }}
                      disabled={hasPendingReview}
                      sx={{ "& .MuiInputBase-root": { minHeight: 56 } }}
                    />
                  </Box>

                  {/* 심사 필드: 자기소개 */}
                  <Box>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                      <Typography variant="body2" fontWeight={600} color="text.secondary">
                        자기소개
                      </Typography>
                      <ReviewFieldBadge />
                    </Box>
                    <TextField
                      fullWidth
                      multiline
                      rows={4}
                      value={formData.selfIntro}
                      onChange={(e) =>
                        setFormData((prev) => ({ ...prev, selfIntro: e.target.value }))
                      }
                      placeholder="자신을 더 자세히 소개해주세요 (10자 이상)"
                      helperText={`${formData.selfIntro.length}/512자`}
                      inputProps={{ maxLength: 512, style: { fontSize: "1rem" } }}
                      disabled={hasPendingReview}
                    />
                  </Box>
                </Stack>
              </Box>

              {/* ---- Section: 거주 지역 (자유 필드 + 연 2회 제한) ---- */}
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    거주 지역
                  </Typography>
                  <FreeFieldBadge />
                  <Typography
                    variant="caption"
                    color={residenceChangesLeft === 0 ? "error" : "text.secondary"}
                    sx={{ ml: "auto" }}
                  >
                    올해 남은 변경 횟수: {residenceChangesLeft}/2회
                  </Typography>
                </Box>

                {residenceChangesLeft === 0 ? (
                  <Alert severity="warning" sx={{ borderRadius: 2 }}>
                    올해 거주지 변경 횟수를 모두 사용했어요. 변경이 필요하면 고객센터에 문의해주세요.
                  </Alert>
                ) : (
                  <Box
                    sx={{
                      display: "flex",
                      gap: 2,
                      flexDirection: { xs: "column", sm: "row" },
                    }}
                  >
                    <FormControl fullWidth>
                      <InputLabel>시/도</InputLabel>
                      <Select
                        value={formData.residenceRegion.level1Id}
                        onChange={(e) => {
                          const selectedLevel1Id = Number(e.target.value);
                          const selectedRegion = level1Regions.find(
                            (r) => r.id === selectedLevel1Id
                          );
                          setFormData((prev) => ({
                            ...prev,
                            residenceRegion: {
                              level1Id: selectedLevel1Id,
                              level1Name: selectedRegion?.nameKo || "",
                              level2Id: 0,
                              level2Name: "",
                            },
                          }));
                        }}
                        label="시/도"
                        sx={{ minHeight: 56 }}
                      >
                        {level1Regions.map((region) => (
                          <MenuItem key={region.id} value={region.id} sx={{ minHeight: 48, fontSize: "1rem" }}>
                            {region.nameKo}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>

                    <FormControl fullWidth>
                      <InputLabel>상세 지역</InputLabel>
                      <Select
                        value={formData.residenceRegion.level2Id}
                        onChange={(e) => {
                          const selectedLevel2Id = Number(e.target.value);
                          const selectedRegion = level2Regions.find(
                            (r) => r.id === selectedLevel2Id
                          );
                          setFormData((prev) => ({
                            ...prev,
                            residenceRegion: {
                              ...prev.residenceRegion,
                              level2Id: selectedLevel2Id,
                              level2Name: selectedRegion?.nameKo || "",
                            },
                          }));
                        }}
                        label="상세 지역"
                        disabled={formData.residenceRegion.level1Id === 0}
                        sx={{ minHeight: 56 }}
                      >
                        {level2Regions.map((region) => (
                          <MenuItem key={region.id} value={region.id} sx={{ minHeight: 48, fontSize: "1rem" }}>
                            {region.nameKo}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                )}
              </Box>

              {/* ---- Section: 선호 조건 (자유 필드) ---- */}
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    선호 조건
                  </Typography>
                  <FreeFieldBadge />
                </Box>
                <FormControl component="fieldset" fullWidth>
                  <FormLabel component="legend" sx={{ fontSize: "1rem", mb: 1 }}>
                    선호 연령대
                  </FormLabel>
                  <RadioGroup
                    value={formData.preferredAge}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        preferredAge: e.target.value as PreferredAge,
                      }))
                    }
                    row
                  >
                    {[PreferredAge.YOUNGER, PreferredAge.OLDER, PreferredAge.ANY].map((value) => (
                      <FormControlLabel
                        key={value}
                        value={value}
                        control={<Radio sx={{ "& .MuiSvgIcon-root": { fontSize: 28 } }} />}
                        label={
                          <Typography sx={{ fontSize: "1rem" }}>
                            {preferredAgeLabels[value]}
                          </Typography>
                        }
                        sx={{ mr: 3 }}
                      />
                    ))}
                  </RadioGroup>
                </FormControl>
              </Box>

              {/* ---- Section: 관심사 (자유 필드) ---- */}
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    관심사
                  </Typography>
                  <FreeFieldBadge />
                </Box>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5 }}>
                  {interests.map((interest) => (
                    <Chip
                      key={interest.id}
                      label={interest.labelKo}
                      clickable
                      color={formData.interestIds.includes(interest.id) ? "primary" : "default"}
                      onClick={() => handleInterestToggle(interest.id)}
                      variant={formData.interestIds.includes(interest.id) ? "filled" : "outlined"}
                      sx={{ fontSize: "0.95rem", height: 40, px: 1 }}
                    />
                  ))}
                </Box>
              </Box>

              {/* ---- Section: 선호 지역 (자유 필드) ---- */}
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    선호 지역
                  </Typography>
                  <FreeFieldBadge />
                </Box>

                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={() => setShowAddRegionDialog(true)}
                  disabled={formData.preferredRegions.length >= 5}
                  sx={{
                    mb: 2,
                    borderStyle: "dashed",
                    borderWidth: 2,
                    borderColor: "#C9A86C",
                    color: "#8A6A40",
                    minHeight: 48,
                    fontSize: "1rem",
                    "&:hover": {
                      backgroundColor: "rgba(201,168,108,0.08)",
                      borderColor: "#0D1F3C",
                    },
                    "&:disabled": { borderColor: "grey.300", color: "grey.400" },
                  }}
                  fullWidth
                >
                  선호 지역 추가하기
                </Button>

                {formData.preferredRegions.length > 0 ? (
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      선택된 지역: {formData.preferredRegions.length}/5
                    </Typography>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {formData.preferredRegions.map((region, index) => (
                        <Chip
                          key={`${region.level1Id}-${region.level2Id}`}
                          label={
                            region.level1Name && region.level2Name
                              ? `${region.level1Name} ${region.level2Name}`
                              : "지역 정보 없음"
                          }
                          onDelete={() => handleRemovePreferredRegion(index)}
                          variant="filled"
                          color="primary"
                          sx={{ fontSize: "0.9rem", height: 36 }}
                        />
                      ))}
                    </Box>
                  </Box>
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ textAlign: "center", py: 1 }}
                  >
                    아직 선택된 지역이 없어요
                  </Typography>
                )}
              </Box>
            </Stack>
          </Box>
        </DialogContent>

        {/* 하단 저장 버튼 (고정) */}
        <DialogActions
          sx={{
            p: 3,
            pt: 2,
            gap: 1,
            borderTop: "1px solid",
            borderColor: "divider",
            position: "sticky",
            bottom: 0,
            bgcolor: "background.paper",
          }}
        >
          <Button
            onClick={onClose}
            variant="outlined"
            disabled={saving}
            sx={{ borderRadius: 2, fontWeight: 700, minWidth: 100, minHeight: 48, fontSize: "1rem" }}
          >
            취소
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            disabled={saving || metaLoading}
            startIcon={
              saving ? <CircularProgress size={20} color="inherit" /> : <Save />
            }
            sx={{
              borderRadius: 2,
              fontWeight: 700,
              minWidth: 120,
              minHeight: 48,
              fontSize: "1rem",
              background: "linear-gradient(135deg, #0D1F3C, #1A3360)",
              "&:hover": { background: "linear-gradient(135deg, #0A1830, #152A50)" },
            }}
          >
            {saving ? submittingLabel : submitLabel}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 선호 지역 추가 다이얼로그 */}
      <Dialog
        open={showAddRegionDialog}
        onClose={handleCloseRegionDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: "20px" } }}
      >
        <Box sx={{ p: 3, pb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            선호 지역 추가
          </Typography>
        </Box>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>시/도</InputLabel>
              <Select
                value={regionDialogLevel1Id}
                label="시/도"
                onChange={(e) => handleRegionDialogLevel1Change(Number(e.target.value))}
                sx={{ minHeight: 56 }}
              >
                <MenuItem value={0} sx={{ minHeight: 48 }}>
                  선택해주세요
                </MenuItem>
                {level1Regions.map((region) => (
                  <MenuItem
                    key={region.id}
                    value={region.id}
                    sx={{ fontSize: "1rem", minHeight: 48 }}
                  >
                    {region.nameKo}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>구/군</InputLabel>
              <Select
                value={regionDialogLevel2Id}
                label="구/군"
                onChange={(e) => setRegionDialogLevel2Id(Number(e.target.value))}
                disabled={!regionDialogLevel1Id}
                sx={{ minHeight: 56 }}
              >
                <MenuItem value={0} sx={{ minHeight: 48 }}>
                  선택해주세요
                </MenuItem>
                {regionDialogLevel2Regions.map((region) => (
                  <MenuItem
                    key={region.id}
                    value={region.id}
                    sx={{ fontSize: "1rem", minHeight: 48 }}
                  >
                    {region.nameKo}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={handleCloseRegionDialog}
            sx={{ minHeight: 48, fontSize: "1rem" }}
          >
            취소
          </Button>
          <Button
            variant="contained"
            onClick={handleAddPreferredRegion}
            disabled={!regionDialogLevel1Id || !regionDialogLevel2Id}
            sx={{ minHeight: 48, fontSize: "1rem" }}
          >
            추가
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
